import { useQuery } from '@tanstack/react-query';
import { useApiClient } from './useApiClient.ts';
import { asOfDayEndMs } from '../lib/asOfDate.ts';

export type MetricTimeRange = '24h' | '7d' | '30d' | '90d';

export interface MetricHistoryDataPoint {
  timestamp: number;
  value: number;
}

interface ApiHistoryResponse {
  metricId: string;
  displayName: string;
  granularity: 'hourly' | 'daily' | 'weekly';
  dataPoints: { periodStart: string; value: number }[];
}

export interface UseMetricHistoryOptions {
  metricId: string;
  timeRange: MetricTimeRange;
  enabled?: boolean;
  /**
   * Extra days fetched before the visible range, for consumers that derive a
   * trailing-window series (rollingWindowSum) and need full window coverage
   * from the first displayed point.
   */
  extraDays?: number;
  /**
   * Point-in-time view (YYYY-MM-DD, UTC day): the window ends at the end of
   * this day instead of now, so sparkline/delta/hi-lo describe the selected
   * date.
   */
  asOf?: string;
}

export interface UseMetricHistoryResult {
  data: {
    dataPoints: MetricHistoryDataPoint[];
    /**
     * Start of the fetched range (visible range plus `extraDays`). Passed to
     * `rollingWindowSum` as `coverageStart` so a young metric (history shorter
     * than the window) charts partial totals rather than drawing nothing
     * until its history is old enough to fill the window.
     */
    rangeStart: number;
  } | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const RANGE_MS: Record<MetricTimeRange, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export function useMetricHistory(
  options: UseMetricHistoryOptions,
): UseMetricHistoryResult {
  const { metricId, timeRange, enabled = true, extraDays = 0, asOf } = options;
  const apiClient = useApiClient();

  const { data = null, isLoading, error, refetch } = useQuery({
    queryKey: ['metricHistory', metricId, timeRange, extraDays, asOf ?? 'live'],
    queryFn: async () => {
      const endTime = asOf !== undefined ? asOfDayEndMs(asOf) : Date.now();
      const startTime = endTime - RANGE_MS[timeRange] - extraDays * 24 * 60 * 60 * 1000;
      const limit = timeRange === '90d' || extraDays > 0 ? 500 : 100;
      const params = new URLSearchParams({
        startTime: String(startTime),
        limit: String(limit),
      });
      if (asOf !== undefined) params.set('endTime', String(endTime));
      const response = await apiClient.get<ApiHistoryResponse>(
        `metrics/${metricId}/history?${params.toString()}`,
      );
      const dataPoints = response.dataPoints
        .map((p) => ({
          timestamp: Date.parse(p.periodStart),
          value: p.value,
        }))
        .sort((a, b) => a.timestamp - b.timestamp);
      return { dataPoints, rangeStart: startTime };
    },
    enabled: enabled && !!metricId,
    // Point-in-time windows are immutable history — no background refresh.
    refetchInterval: asOf !== undefined ? false : 5 * 60 * 1000,
    placeholderData: (previous) => previous,
  });

  return { data, isLoading, error: error as Error | null, refetch };
}
